import { Filter } from '@cubejs-client/core'
import {
  CubeFilterDimensions,
  DRIFT_CHAT_CONVERSATIONS_RATINGS_MEASURES,
  ROUTING_MEASURES,
} from 'cube'

export const NODE_CONSTANTS = Object.freeze({
  ROUTING_REQUESTS: 'Routing Requests',
  TEAMMATES_ROUTED_IN: 'Teammate Routed In',
  TEAMMATES_NOT_ROUTED_IN: 'Teammate Not Routed In',
  RESPONDED_WITHIN_60S: 'Responded Within 60s',
  MISSED: 'Missed',
  RESPONSE_LATE: 'Responded Late',
  NO_RESPONSE: 'No Response',
  TEAMMATE_RESPONDED: 'Teammate Responded',
  CALENDAR_DROP: 'Calendar Drop',
  DEAD_END: 'Dead End',
  TEAMMATES_UNAVAILABLE: 'Teammates Unavailable',
  AWAY_MESSAGE_SENT: 'Away Message Sent',
  CREATE_TICKET: 'Create Ticket',
})

export const NODE_DESCRIPTIONS = Object.freeze({
  [NODE_CONSTANTS.ROUTING_REQUESTS]:
    'A routing request occurs when a visitor wants to chat with someone and the routing rules fire.',
  [NODE_CONSTANTS.TEAMMATES_ROUTED_IN]:
    'A teammate routed in occurs when a teammate was added to a live chat.',
  [NODE_CONSTANTS.TEAMMATES_NOT_ROUTED_IN]:
    'Teammate not routed occurs when no teammate was added to a live chat.',
  [NODE_CONSTANTS.RESPONDED_WITHIN_60S]:
    'Responded within 60s occurs when the teammate’s first reply was sent under 60s.',
  [NODE_CONSTANTS.MISSED]:
    'Missed occurs when the teammate never replies or first reply took longer than 60s.',
  [NODE_CONSTANTS.RESPONSE_LATE]:
    'Responded late occurs when the teammate’s first reply took longer than 60s.',
  [NODE_CONSTANTS.NO_RESPONSE]:
    'No response occurs when no one responds to the site visitor after a teammate was routed into the chat.',
  [NODE_CONSTANTS.TEAMMATE_RESPONDED]:
    'Teammate responded occurs when one or more teammates respond to the site visitor after an agent was first routed in (irrespective of response time).',
  [NODE_CONSTANTS.CALENDAR_DROP]:
    'Calendar drop occurs when we drop (or attempted to drop) a teammate’s calendar so that the site visitor can book a meeting with them.',
  [NODE_CONSTANTS.DEAD_END]:
    'A dead end occurs when the site visitor gets no teammate replies or fallback-related messages after one or more teammates were routed into the chat.',
  [NODE_CONSTANTS.TEAMMATES_UNAVAILABLE]:
    'A teammate is unavailable when a chat is routed during online hours, but all individual teammates were either offline or at their chat limit.',
  [NODE_CONSTANTS.AWAY_MESSAGE_SENT]:
    'Away message sent occurs when we send an away message to the site visitor as configured by a routing fallback.',
  [NODE_CONSTANTS.CREATE_TICKET]:
    'Create ticket occurs when we allow the site visitor to create a ticket/case as configured by a routing fallback.',
})

// gradients for the paths in the sankey
export const SANKEY_GRADIENT_COLORS = Object.freeze({
  purpleBlue:
    'url(#sankey-color-gradient-purpleBlue) var(--drift-dds-colors-purple8)',
  blueTeal:
    'url(#sankey-color-gradient-blueTeal) var(--drift-dds-colors-blue8)',
  tealGreen:
    'url(#sankey-color-gradient-tealGreen) var(--drift-dds-colors-teal5)',
  blueOrange:
    'url(#sankey-color-gradient-blueOrange) var(--drift-dds-colors-blue8)',
  tealYellow:
    'url(#sankey-color-gradient-tealYellow) var(--drift-dds-colors-teal5)',
  yellowGreen:
    'url(#sankey-color-gradient-yellowGreen) var(--drift-dds-colors-yellow6)',
  orangeBlue:
    'url(#sankey-color-gradient-orangeBlue) var(--drift-dds-colors-orange6)',
  yellowBlue:
    'url(#sankey-color-gradient-yellowBlue) var(--drift-dds-colors-yellow6)',
  yellowRed:
    'url(#sankey-color-gradient-yellowRed) var(--drift-dds-colors-yellow6)',
  orangeRed:
    'url(#sankey-color-gradient-orangeRed) var(--drift-dds-colors-orange6)',
  orangeYellow:
    'url(#sankey-color-gradient-orangeYellow) var(--drift-dds-colors-orange6)',
  redBlue: 'url(#sankey-color-gradient-redBlue) var(--drift-dds-colors-red8)',
  blueGreen:
    'url(#sankey-color-gradient-blueGreen) var(--drift-dds-colors-blue8)',
  tealBlue:
    'url(#sankey-color-gradient-tealBlue) var(--drift-dds-colors-teal5)',
  tealRed: 'url(#sankey-color-gradient-tealRed) var(--drift-dds-colors-teal5)',
  red: 'var(--drift-dds-colors-red8)',
  green: 'var(--drift-dds-colors-green6)',
})

// mapping to edge names in cube
const SANKEY_EDGE_NAMES = Object.freeze({
  routingRequestToTeammateRoutedIn:
    'DriftChatConversationRouting.countRoutingRequestToTeammateRoutedIn',
  routingRequestToTeammateNotRoutedIn:
    'DriftChatConversationRouting.countRoutingRequestToTeammateNotRoutedIn',
  teammateRoutedInToRespondedWithin60s:
    'DriftChatConversationRouting.countTeammateRoutedInToRespondedWithin60s',
  teammateRoutedInToMissed:
    'DriftChatConversationRouting.countTeammateRoutedInToMissed',
  respondedWithin60sToTeammateResponded:
    'DriftChatConversationRouting.countRespondedWithin60sToTeammateResponded',
  missedToResponseLate:
    'DriftChatConversationRouting.countMissedToResponseLate',
  missedToNeverResponded:
    'DriftChatConversationRouting.countMissedToNeverResponded',
  respondedLateToTeammateResponded:
    'DriftChatConversationRouting.countRespondedLateToTeammateResponded',
  neverRespondedToCalendarDropped:
    'DriftChatConversationRouting.countNeverRespondedToCalendarDropped',
  neverRespondedToDeadEnd:
    'DriftChatConversationRouting.countNeverRespondedToDeadEnd',
  teammateNotRoutedInToTeammatesWereUnavailable:
    'DriftChatConversationRouting.countTeammateNotRoutedInToTeammatesWereUnavailable',
  teammatesWereUnavailableToAwayMessageSent:
    'DriftChatConversationRouting.countTeammatesWereUnavailableToAwayMessageSent',
  teammatesWereUnavailableToTicketCreated:
    'DriftChatConversationRouting.countTeammatesWereUnavailableToTicketCreated',
  teammatesWereUnavailableToDeadEnd:
    'DriftChatConversationRouting.countTeammatesWereUnavailableToDeadEnd',
  neverRespondedToAwayMessageSent:
    'DriftChatConversationRouting.countNeverRespondedToAwayMessageSent',
  neverRespondedToTicketCreated:
    'DriftChatConversationRouting.countNeverRespondedToTicketCreated',
  teammatesWereUnavailableToCalendarDropped:
    'DriftChatConversationRouting.countTeammatesWereUnavailableToCalendarDropped',
  teammatesWereUnavailableToTeammateResponded:
    'DriftChatConversationRouting.countTeammatesWereUnavailableToTeammateResponded',
})

// map of edge names and their from -> to nodes
export const SANKEY_EDGE_TO_NODES_MAP = Object.freeze({
  [SANKEY_EDGE_NAMES.routingRequestToTeammateRoutedIn]: [
    NODE_CONSTANTS.ROUTING_REQUESTS,
    NODE_CONSTANTS.TEAMMATES_ROUTED_IN,
  ],
  [SANKEY_EDGE_NAMES.routingRequestToTeammateNotRoutedIn]: [
    NODE_CONSTANTS.ROUTING_REQUESTS,
    NODE_CONSTANTS.TEAMMATES_NOT_ROUTED_IN,
  ],
  [SANKEY_EDGE_NAMES.teammateRoutedInToRespondedWithin60s]: [
    NODE_CONSTANTS.TEAMMATES_ROUTED_IN,
    NODE_CONSTANTS.RESPONDED_WITHIN_60S,
  ],
  [SANKEY_EDGE_NAMES.teammateRoutedInToMissed]: [
    NODE_CONSTANTS.TEAMMATES_ROUTED_IN,
    NODE_CONSTANTS.MISSED,
  ],
  [SANKEY_EDGE_NAMES.respondedWithin60sToTeammateResponded]: [
    NODE_CONSTANTS.RESPONDED_WITHIN_60S,
    NODE_CONSTANTS.TEAMMATE_RESPONDED,
  ],
  [SANKEY_EDGE_NAMES.missedToResponseLate]: [
    NODE_CONSTANTS.MISSED,
    NODE_CONSTANTS.RESPONSE_LATE,
  ],
  [SANKEY_EDGE_NAMES.missedToNeverResponded]: [
    NODE_CONSTANTS.MISSED,
    NODE_CONSTANTS.NO_RESPONSE,
  ],
  [SANKEY_EDGE_NAMES.respondedLateToTeammateResponded]: [
    NODE_CONSTANTS.RESPONSE_LATE,
    NODE_CONSTANTS.TEAMMATE_RESPONDED,
  ],
  [SANKEY_EDGE_NAMES.neverRespondedToCalendarDropped]: [
    NODE_CONSTANTS.NO_RESPONSE,
    NODE_CONSTANTS.CALENDAR_DROP,
  ],
  [SANKEY_EDGE_NAMES.neverRespondedToDeadEnd]: [
    NODE_CONSTANTS.NO_RESPONSE,
    NODE_CONSTANTS.DEAD_END,
  ],
  [SANKEY_EDGE_NAMES.teammateNotRoutedInToTeammatesWereUnavailable]: [
    NODE_CONSTANTS.TEAMMATES_NOT_ROUTED_IN,
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
  ],
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToAwayMessageSent]: [
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
    NODE_CONSTANTS.AWAY_MESSAGE_SENT,
  ],
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToTicketCreated]: [
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
    NODE_CONSTANTS.CREATE_TICKET,
  ],
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToDeadEnd]: [
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
    NODE_CONSTANTS.DEAD_END,
  ],
  [SANKEY_EDGE_NAMES.neverRespondedToAwayMessageSent]: [
    NODE_CONSTANTS.NO_RESPONSE,
    NODE_CONSTANTS.AWAY_MESSAGE_SENT,
  ],
  [SANKEY_EDGE_NAMES.neverRespondedToTicketCreated]: [
    NODE_CONSTANTS.NO_RESPONSE,
    NODE_CONSTANTS.CREATE_TICKET,
  ],
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToCalendarDropped]: [
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
    NODE_CONSTANTS.CALENDAR_DROP,
  ],
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToTeammateResponded]: [
    NODE_CONSTANTS.TEAMMATES_UNAVAILABLE,
    NODE_CONSTANTS.TEAMMATE_RESPONDED,
  ],
})

export const SANKEY_EDGE_COLOR_MAP = Object.freeze({
  [SANKEY_EDGE_NAMES.routingRequestToTeammateRoutedIn]:
    SANKEY_GRADIENT_COLORS.purpleBlue,
  [SANKEY_EDGE_NAMES.routingRequestToTeammateNotRoutedIn]:
    SANKEY_GRADIENT_COLORS.purpleBlue,
  [SANKEY_EDGE_NAMES.teammateRoutedInToRespondedWithin60s]:
    SANKEY_GRADIENT_COLORS.blueGreen,
  [SANKEY_EDGE_NAMES.teammateRoutedInToMissed]:
    SANKEY_GRADIENT_COLORS.blueOrange,
  [SANKEY_EDGE_NAMES.respondedWithin60sToTeammateResponded]:
    SANKEY_GRADIENT_COLORS.green,
  [SANKEY_EDGE_NAMES.missedToResponseLate]: SANKEY_GRADIENT_COLORS.orangeYellow,
  [SANKEY_EDGE_NAMES.missedToNeverResponded]: SANKEY_GRADIENT_COLORS.orangeRed,
  [SANKEY_EDGE_NAMES.respondedLateToTeammateResponded]:
    SANKEY_GRADIENT_COLORS.yellowGreen,
  [SANKEY_EDGE_NAMES.neverRespondedToCalendarDropped]:
    SANKEY_GRADIENT_COLORS.redBlue,
  [SANKEY_EDGE_NAMES.neverRespondedToDeadEnd]: SANKEY_GRADIENT_COLORS.red,
  [SANKEY_EDGE_NAMES.teammateNotRoutedInToTeammatesWereUnavailable]:
    SANKEY_GRADIENT_COLORS.blueTeal,
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToAwayMessageSent]:
    SANKEY_GRADIENT_COLORS.tealBlue,
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToTicketCreated]:
    SANKEY_GRADIENT_COLORS.tealBlue,
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToDeadEnd]:
    SANKEY_GRADIENT_COLORS.tealRed,
  [SANKEY_EDGE_NAMES.neverRespondedToAwayMessageSent]:
    SANKEY_GRADIENT_COLORS.redBlue,
  [SANKEY_EDGE_NAMES.neverRespondedToTicketCreated]:
    SANKEY_GRADIENT_COLORS.redBlue,
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToCalendarDropped]:
    SANKEY_GRADIENT_COLORS.tealBlue,
  [SANKEY_EDGE_NAMES.teammatesWereUnavailableToTeammateResponded]:
    SANKEY_GRADIENT_COLORS.tealGreen,
})

// Maps routing schema measures to their corresponding dimension filters. Primarily used to format drill-down queries.
export const MEASURE_FILTER_MAP: Record<string, Filter[]> = Object.freeze({
  [DRIFT_CHAT_CONVERSATIONS_RATINGS_MEASURES.RATING_AVERAGE]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.CSAT_RATING,
      operator: 'equals',
      values: ['1', '2', '3', '4', '5'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATE_ROUTED_IN_TO_RESPONDED_WITHIN60S]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
      operator: 'lte',
      values: ['60'],
    },
  ],
  [ROUTING_MEASURES.RESPONSE_COUNT]: [
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.DEAD_END_COUNT]: [
    {
      member: CubeFilterDimensions.IS_DEAD_END_CONVERSATION,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.ROUTING_REQUEST_TO_TEAMMATE_ROUTED_IN]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.ROUTING_REQUEST_TO_TEAMMATE_NOT_ROUTED_IN]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATE_ROUTED_IN_TO_MISSED]: [
    {
      member: CubeFilterDimensions.HAS_ROUTED_AND_EVERYONE_MISSED,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.MISSED_TO_RESPONSE_LATE]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
      operator: 'gt',
      values: ['60'],
    },
  ],
  [ROUTING_MEASURES.MISSED_TO_NEVER_RESPONDED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['false'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATE_NOT_ROUTED_IN_TO_TEAMMATES_WERE_UNAVAILABLE]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
  ],
  [ROUTING_MEASURES.RESPONDED_WITHIN60S_TO_TEAMMATE_RESPONDED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
      operator: 'lte',
      values: ['60'],
    },
  ],
  [ROUTING_MEASURES.RESPONDED_LATE_TO_TEAMMATE_RESPONDED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TIME_TO_FIRST_RESPONSE_S,
      operator: 'gt',
      values: ['60'],
    },
  ],
  [ROUTING_MEASURES.NEVER_RESPONDED_TO_AWAY_MESSAGE_SENT]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['AWAY_MESSAGE_SENT'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATES_WERE_UNAVAILABLE_TO_AWAY_MESSAGE_SENT]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['AWAY_MESSAGE_SENT'],
    },
  ],
  [ROUTING_MEASURES.NEVER_RESPONDED_TO_CALENDAR_DROPPED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['CALENDAR_DROP'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATES_WERE_UNAVAILABLE_TO_CALENDAR_DROPPED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['CALENDAR_DROP'],
    },
  ],
  [ROUTING_MEASURES.NEVER_RESPONDED_TO_TICKET_CREATED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['TICKET_CREATED'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATES_WERE_UNAVAILABLE_TO_TICKET_CREATED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.CONVERSATION_ROUTING_OUTCOME,
      operator: 'equals',
      values: ['TICKET_CREATED'],
    },
  ],
  [ROUTING_MEASURES.NEVER_RESPONDED_TO_DEAD_END]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['true'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.IS_DEAD_END_CONVERSATION,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATES_WERE_UNAVAILABLE_TO_DEAD_END]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.IS_DEAD_END_CONVERSATION,
      operator: 'equals',
      values: ['true'],
    },
  ],
  [ROUTING_MEASURES.TEAMMATES_WERE_UNAVAILABLE_TO_TEAMMATE_RESPONDED]: [
    {
      member: CubeFilterDimensions.CONVERSATION_HAD_TEAMMATE_ROUTED_IN,
      operator: 'equals',
      values: ['false'],
    },
    {
      member: CubeFilterDimensions.TEAMMATE_RESPONDED,
      operator: 'equals',
      values: ['true'],
    },
  ],
})
